import {
  DialogPositioningBehavior,
  DialogProps,
  DialogType,
  DialogWidget,
  HorizontalOriginOption,
  HostedStackedElementContainer,
  StackingRootContext,
  VerticalOriginOption,
  OriginOptions,
} from '@tableau/widgets';
import * as React from 'react';
import * as _ from 'underscore';

import { Provider } from 'react-redux';
import { appStore } from '../../app/AppStore';
import { Messages } from '../../codegen/Localize';

/**
 * These options are mostly a subset of the DialogProps interface of the shared widgets dialog.
 * See their documentation for descriptions (http://devmockup/visualDesign/widgets.html)
 */
export interface ReactModalOptions {
  type: DialogType;
  dialogContent: React.ReactNode; // the content of the dialog
  testId?: string;
  title?: string; // this really should be required but there are too many dialogs to update all of these at once
  showCloseButton?: boolean;
  positioningBehavior?: DialogPositioningBehavior; // does not support Anchored
  dialogStyle?: React.CSSProperties;
  dialogContentStyle?: React.CSSProperties;
  dialogBodyStyle?: React.CSSProperties;
  dialogWidth?: number; // defaults to 400px
  footerContent?: React.ReactNode;
  dismissDialogOnCloseAction?: boolean; // Call dismiss() instead of close() on close request or glass click
  shouldNotCloseGlass?: boolean;    // Ignore close on glass
  shouldReturnFocusOnClose?: boolean;
  shouldNotSetDefaultWidth?: boolean;
  shouldRecenterOnUpdate?: boolean;
  disableAutoFocus?: boolean;
  getAnchorClientRect?: () => ClientRect;
  anchorOrigin?: OriginOptions;
}

export interface ModalInstance {
  close: (result?: any) => void;
  dismiss: (reason?: any) => void;
  result: ng.IPromise<any>;
}

export interface ReactModalServiceDialogProps {
  close?: (result?: any) => void;   // Close will be available to the dialog content
}

// The reference to this immutable array gets overwritten as dialogs are open or closed
let modalInstances: ReadonlyArray<ModalInstance> = [];

/**
 * Factory for creating stacking containers. It's extracted so we can stub it in unit tests,
 * because dialogs rendered using the stacking container can't be easily accessed via Enzyme.
 */
export namespace HostedStackedElementContainerFactory {
  export function newStackedElementContainer() {
    return new HostedStackedElementContainer();
  }
}

/**
 * Opens a modal dialog with arbitrary content and properties.
 *
 * For dialogs with an OK/Cancel button, consider using `ReactConfirmActionDialog`, which provides
 * a simpler interface for this common use case.
 */
function open(opts: ReactModalOptions): ModalInstance {

  const deferredResult = Q.defer();
  const hostContainer = HostedStackedElementContainerFactory.newStackedElementContainer();

  const closeDialog = () => {
    hostContainer && hostContainer.remove();
    modalInstances = _.without(modalInstances, modalInstance);
  };

  const modalInstance: ModalInstance = {
    result: deferredResult.promise as ng.IPromise<any>,
    close: (result?: any) => {
      closeDialog();
      deferredResult.resolve(result);
    },
    dismiss: (reason?: any) => {
      closeDialog();
      deferredResult.reject(reason);
    },
  };

  let dialogWidth;
  if (opts.shouldNotSetDefaultWidth) {
    dialogWidth = opts.dialogWidth;
  } else {
    dialogWidth = opts.dialogWidth || 400;
  }

  const closeOrDismiss = opts.dismissDialogOnCloseAction ? modalInstance.dismiss : modalInstance.close;

  const dialogProps: DialogProps = {
    testId: opts.testId || 'modal',
    type: opts.type,
    title: opts.title || Messages.dialogTitle_tryToAvoidThis(),
    showCloseButton: opts.showCloseButton,
    onCloseRequest: closeOrDismiss,
    positioningBehavior: opts.positioningBehavior || DialogPositioningBehavior.Centered,
    isModal: true,
    handleGlassClick: !opts.shouldNotCloseGlass ? closeOrDismiss : undefined,
    dialogStyle: opts.dialogStyle,
    dialogContentStyle: opts.dialogContentStyle,
    dialogBodyStyle: opts.dialogBodyStyle,
    dialogWidth: dialogWidth,
    showFooter: !!opts.footerContent,
    footerContent: opts.footerContent,
    shouldAdjustPositionOnWindowResize: true,
    shouldReturnFocusOnClose: opts.shouldReturnFocusOnClose,
    disableAutoFocus: opts.disableAutoFocus,
    getAnchorClientRect: opts.getAnchorClientRect,
    anchorOrigin: opts.anchorOrigin
  };

  const dialogContentComponent = convertNodeToReactElement(opts.dialogContent);
  const connectedDialogContent = createConnectedDialogComponent(dialogContentComponent, dialogProps, modalInstance.close);
  hostContainer.render(connectedDialogContent);

  modalInstances = modalInstances.concat(modalInstance);
  return modalInstance;
}

function convertNodeToReactElement(dialogContent: React.ReactNode): React.ReactElement {
  if (typeof dialogContent !== 'object') {
    return <React.Fragment>{dialogContent}</React.Fragment>;
  } else {
    return dialogContent as React.ReactElement;
  }
}

/**
 * Allows dialogs to use the redux store and dispatch actions
 * Also adds a close function so the dialog can close itself.
 *
 * The Provider is needed because the StackedElementContainer exists outside of
 * the <App /> which has it's own <Provider>
 * 
 * The StackingRootContext is needed to instantiate the dialog in the #ng-app div.
 * Without wrapping by StackingRootContext, the dialog would be rendered in 
 * DOM body by default.
 */
function createConnectedDialogComponent(dialogContentComponent: React.ReactElement, dialogProps: DialogProps, closeFn: (result?: any) => void) {
  const dialogContentComponentWithClose = React.cloneElement(dialogContentComponent, { close: closeFn });
  const stackingRoot = document.getElementById('ng-app');
  return (
    <StackingRootContext.Provider value={stackingRoot}>
      <Provider store={appStore}>
        <DialogWidget {...dialogProps}>{dialogContentComponentWithClose}</DialogWidget>
      </Provider>
    </StackingRootContext.Provider>
  );
}

/**
 * Registers an object as a modal with the ReactModalService, meaning it will
 * automatically have it's dismiss method (or close if dismissed is not
 * provided) called upon switching pages.The only necessary parameter is close,
 * other parameters can be provided if you also intend to access the modal
 * through the ReactModalService in other cases or use the ReactModalService to
 * track the state of your modal and read from it (as the ReactModalService
 * itself will not actually do anything with your modal other than close it for
 * you when the page changes.
 *
 * @param close A function that closes the dialog
 * @param dismiss A function that dismisses the dialog, similar to close
 * @param result An angular promise containing a result
 */
function registerModal(close: (reason?: any) => void, dismiss?: (reason?: any) => void, result?: ng.IPromise<any>) {
  modalInstances = modalInstances.concat({
    close: close,
    dismiss: dismiss || close,
    result: result || Q.defer().promise as ng.IPromise<any>,
  });
}

function dismissAll(reason?: any) {
  modalInstances.forEach(m => m.dismiss(reason));
  modalInstances = [];
}


/**
 * A service that opens React shared-widgets modals. The interface is designed to match
 * ModalService as closely as possible.
 */
export const ReactModalService = { open, dismissAll, registerModal };
